import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MessageSquarePlus } from 'lucide-react';
import type { TicketDetail, TicketRepair, SuppliedItem, PatchTicketDeviceBody } from '@/lib/api/tickets.js';
import {
  addLineItem,
  addNote,
  assignTicket,
  deleteDevicePhoto,
  deleteLineItem,
  deleteTicketDevice,
  patchSuppliedItems,
  patchTicketDevice,
  sendTicketEmail,
  sendTicketSms,
  updateLineItem,
  updateRepair,
  deleteRepair,
  uploadDevicePhoto,
} from '@/lib/api/tickets.js';
import { listFeePresets } from '@/lib/api/fee-presets.js';
import { getTicketParts, removeTicketPart } from '@/lib/api/ticket-parts.js';
import { getCurrentStore, listStores } from '@/lib/api/stores.js';
import { useMultiStore } from '@/hooks/useMultiStore.js';
import { listDeviceConditionTemplates } from '@/lib/api/device-condition-templates.js';
import type { PartsRequest } from '@/lib/api/parts-requests.js';
import type { UserListItem } from '@/lib/api/users.js';
import { CompactTicketHeader } from '@/components/ticket-detail/CompactTicketHeader.js';
import { TicketIntakeContext } from '@/components/ticket-detail/TicketIntakeContext.js';
import { CloneTicketModal } from '@/components/tickets/CloneTicketModal.js';
import { isManagerRole } from '@/lib/permissions.js';
import { TicketFormsSection } from '@/components/ticket-detail/TicketFormsSection.js';
import { TicketDetailSidebar } from '@/components/ticket-detail/TicketDetailSidebar.js';
import { TicketEstimatesPanel } from '@/components/ticket-detail/TicketEstimatesPanel.js';
import { DeviceCardDetail } from '@/components/ticket-detail/DeviceCardDetail.js';
import { DeviceTabs } from '@/components/ticket-detail/DeviceTabs.js';
import { CommunicationTimeline } from '@/components/ticket-detail/CommunicationTimeline.js';
import { TicketComposeFields, type ComposeMode } from '@/components/ticket-detail/TicketComposeFields.js';
import { BottomSheet } from '@/components/ui/BottomSheet.js';
import { SendCareGuideModal } from '@/components/tickets/SendCareGuideModal.js';
import { TransferTicketModal } from '@/components/tickets/TransferTicketModal.js';
import { TicketTransferBanner } from '@/components/tickets/TicketTransferBanner.js';
import { PredictiveAlertsBanner } from '@/components/tickets/PredictiveAlertsBanner.js';
import type { PredictiveAlert } from '@/lib/api/tickets.js';
import { formatCurrency } from '@/lib/format.js';
import { linePreTaxTotal } from '@/lib/money/line-totals.js';
import { parseApiError } from '@/lib/api/errors.js';
import { isUnderWarranty } from '@fixflow/shared-types';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import {
  listInventoryItems,
  scanBarcode,
  type InventoryItemListRow,
} from '@/lib/api/inventory.js';
import {
  buildTicketPortalUrl,
  copyTicketPortalLink,
  openPortalSmsCompose,
} from '@/lib/portal-url.js';
import type { KeyboardEvent } from 'react';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function lineTypeFromItem(item: InventoryItemListRow): 'part' | 'retail' {
  return item.category === 'repair_part' ? 'part' : 'retail';
}

function normalizeCategory(category?: string | null) {
  if (!category) return 'phone';
  if (category === 'computer') return 'laptop';
  if (category === 'watch') return 'phone';
  return category;
}

function mergeSuppliedItems(templateNames: string[], saved: SuppliedItem[]): SuppliedItem[] {
  const savedMap = new Map(saved.map((i) => [i.name, i]));
  const merged = templateNames.map((name) => savedMap.get(name) ?? { name, checked: false });
  for (const item of saved) {
    if (!templateNames.includes(item.name)) merged.push(item);
  }
  return merged;
}

export function TicketDetailRepairView({
  detail,
  users,
  partsRequests,
  customerName,
  onStatusChange,
  onClearOverride,
  onSwitchToBench,
  onPriorityChange,
  onCheckout,
  onCreateWarranty,
  onCollectDeposit,
  onOrderPart,
  onRefetch,
  onVoid,
  onDelete,
  canVoid,
  canDelete,
  timelineScrollRef,
  predictiveAlerts = [],
  onDismissPredictiveAlerts,
  onCollectPreWaiver,
  onCollectPostWaiver,
}: {
  detail: TicketDetail;
  users: UserListItem[];
  partsRequests: PartsRequest[];
  customerName: string;
  onStatusChange: (status: string) => void;
  onClearOverride?: () => void;
  onSwitchToBench?: () => void;
  onPriorityChange: (priority: string) => void;
  onCheckout: () => void;
  onCreateWarranty: () => void;
  onCollectDeposit: () => void;
  onOrderPart: (repair: TicketRepair) => void;
  onRefetch: () => void;
  onVoid: () => void;
  onDelete?: () => void;
  canVoid: boolean;
  canDelete?: boolean;
  timelineScrollRef?: React.RefObject<HTMLDivElement | null>;
  predictiveAlerts?: PredictiveAlert[];
  onDismissPredictiveAlerts?: () => void;
  onCollectPreWaiver?: () => void;
  onCollectPostWaiver?: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ticket = detail.ticket;
  const [careGuideOpen, setCareGuideOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState<ComposeMode>('private');
  const [composeBody, setComposeBody] = useState('');
  const [smsPhone, setSmsPhone] = useState(detail.customer?.phone ?? ticket.customerPhoneOverride ?? '');
  const [emailTo, setEmailTo] = useState(detail.customer?.email ?? ticket.customerEmailOverride ?? '');
  const [emailSubject, setEmailSubject] = useState(`Re: ${ticket.ticketNumber}`);
  const [lineDescription, setLineDescription] = useState('');
  const [lineQty, setLineQty] = useState('1');
  const [linePrice, setLinePrice] = useState('0');
  const [inventorySearch, setInventorySearch] = useState('');
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<InventoryItemListRow | null>(
    null,
  );
  const debouncedInventorySearch = useDebouncedValue(inventorySearch, 250);
  const [feePanelOpen, setFeePanelOpen] = useState(false);
  const [customFeeOpen, setCustomFeeOpen] = useState(false);
  const [feeDescription, setFeeDescription] = useState('');
  const [feeQty, setFeeQty] = useState('1');
  const [feePrice, setFeePrice] = useState('0');
  const timelineRef = timelineScrollRef ?? useRef<HTMLDivElement>(null);
  const inventorySearchWrapRef = useRef<HTMLDivElement>(null);
  const [inventoryDropdownOpen, setInventoryDropdownOpen] = useState(false);

  const feePresetsQuery = useQuery({
    queryKey: ['fee-presets'],
    queryFn: () => listFeePresets(),
  });

  const inventoryPickerQuery = useQuery({
    queryKey: ['ticket-additional-items-search', debouncedInventorySearch],
    queryFn: () =>
      listInventoryItems({
        search: debouncedInventorySearch,
        page: 1,
        pageSize: 12,
        sellableOnly: true,
      }),
    enabled: debouncedInventorySearch.trim().length >= 2,
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        inventorySearchWrapRef.current &&
        !inventorySearchWrapRef.current.contains(event.target as Node)
      ) {
        setInventoryDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const storeQuery = useQuery({
    queryKey: ['store-current'],
    queryFn: getCurrentStore,
  });

  const storesQuery = useQuery({
    queryKey: ['stores-list'],
    queryFn: listStores,
  });

  const canClone = isManagerRole(user?.role ?? '');
  const { isMultiStore } = useMultiStore();
  const canTransfer = useMemo(() => {
    if (!isMultiStore) return false;
    const allStores = storesQuery.data ?? [];
    const assignedStoreIds = user?.storeIds;
    const available =
      user?.role === 'tenant_admin' || user?.role === 'platform_admin'
        ? allStores.filter((s) => s.active && s.id !== user?.storeId)
        : allStores.filter(
            (s) => s.active && s.id !== user?.storeId && assignedStoreIds?.includes(s.id),
          );
    return available.length > 0 && !['closed', 'cancelled', 'voided'].includes(ticket.status);
  }, [isMultiStore, storesQuery.data, user, ticket.status]);

  const partsQuery = useQuery({
    queryKey: ['ticket-parts', ticket.id],
    queryFn: () => getTicketParts(ticket.id),
  });

  const partsByRepair = useMemo(() => {
    const map = new Map<string, import('@/lib/api/ticket-parts.js').TicketPart[]>();
    for (const group of partsQuery.data?.data ?? []) {
      map.set(group.repairId, group.parts);
    }
    return map;
  }, [partsQuery.data]);

  const removePartMut = useMutation({
    mutationFn: (partId: string) => removeTicketPart(ticket.id, partId),
    onSuccess: () => {
      toastSuccess('Part removed');
      partsQuery.refetch();
      onRefetch();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const warrantyActive =
    ticket.status === 'closed' &&
    isUnderWarranty({
      type: ticket.warrantyType,
      days: ticket.warrantyDays,
      expiresAt: ticket.warrantyExpiresAt,
    });

  const primaryCategory = normalizeCategory(detail.devices[0]?.category);
  const templatesQuery = useQuery({
    queryKey: ['device-condition-templates', primaryCategory],
    queryFn: () => listDeviceConditionTemplates({ deviceCategory: primaryCategory }),
  });

  const conditionTemplate = useMemo(() => {
    const row = templatesQuery.data?.find((t) => t.templateType === 'conditions');
    return [...(row?.items ?? [])].sort((a, b) => a.order - b.order);
  }, [templatesQuery.data]);

  const suppliedTemplate = useMemo(() => {
    const row = templatesQuery.data?.find((t) => t.templateType === 'supplied_items');
    return [...(row?.items ?? [])].sort((a, b) => a.order - b.order);
  }, [templatesQuery.data]);

  const suppliedItems = useMemo(
    () => mergeSuppliedItems(suppliedTemplate.map((i) => i.name), detail.suppliedItems ?? []),
    [suppliedTemplate, detail.suppliedItems],
  );

  const repairsByDevice = useMemo(() => {
    const grouped: Record<string, TicketRepair[]> = {};
    for (const repair of detail.repairs) {
      const deviceId = repair.ticketDeviceId ?? 'unassigned';
      grouped[deviceId] = [...(grouped[deviceId] ?? []), repair];
    }
    return grouped;
  }, [detail.repairs]);

  const devices = detail.devices;
  const multiDevice = devices.length > 1;
  const selectedDeviceId = useMemo(() => {
    if (!multiDevice) return devices[0]?.id;
    const fromUrl = searchParams.get('device');
    if (fromUrl && devices.some((d) => d.id === fromUrl)) return fromUrl;
    return devices[0]?.id;
  }, [multiDevice, devices, searchParams]);
  const selectedDevice = devices.find((d) => d.id === selectedDeviceId) ?? devices[0];

  function selectDevice(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('device', id);
    setSearchParams(next, { replace: true });
  }

  const additionalLineItems = detail.lineItems.filter(
    (line) => ['retail', 'fee', 'custom', 'labor', 'part'].includes(line.type) && !line.ticketRepairId,
  );

  const patchDeviceMut = useMutation({
    mutationFn: ({ deviceId, body }: { deviceId: string; body: PatchTicketDeviceBody }) =>
      patchTicketDevice(ticket.id, deviceId, body),
    onSuccess: (result) => {
      if (result.partsWarning) toastError(result.partsWarning);
      onRefetch();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deleteDeviceMut = useMutation({
    mutationFn: (args: { deviceId: string; reassignToDeviceId?: string }) => {
      if (args.reassignToDeviceId) {
        return deleteTicketDevice(ticket.id, args.deviceId, {
          reassignToDeviceId: args.reassignToDeviceId,
        });
      }
      return deleteTicketDevice(ticket.id, args.deviceId);
    },
    onSuccess: () => {
      onRefetch();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const onPatchDevice = useCallback(
    async (deviceId: string, body: PatchTicketDeviceBody): Promise<void> => {
      await patchDeviceMut.mutateAsync({ deviceId, body });
    },
    [patchDeviceMut],
  );

  const onRemoveDevice = useCallback(
    async (deviceId: string, reassignToDeviceId?: string): Promise<void> => {
      if (reassignToDeviceId) {
        await deleteDeviceMut.mutateAsync({ deviceId, reassignToDeviceId });
        return;
      }
      await deleteDeviceMut.mutateAsync({ deviceId });
    },
    [deleteDeviceMut],
  );

  const patchSuppliedMut = useMutation({
    mutationFn: (items: SuppliedItem[]) => patchSuppliedItems(ticket.id, items),
    onError: (e) => toastError(parseApiError(e).message),
  });

  const onPatchSuppliedItems = useCallback(
    (items: SuppliedItem[]) => {
      patchSuppliedMut.mutate(items);
    },
    [patchSuppliedMut],
  );

  const photoMut = useMutation({
    mutationFn: (args: { deviceId: string; photoType: 'pre' | 'post'; dataUrl: string; caption: string }) =>
      uploadDevicePhoto(ticket.id, args.deviceId, {
        photoType: args.photoType,
        dataUrl: args.dataUrl,
        caption: args.caption,
      }),
    onSuccess: () => {
      toastSuccess('Photo uploaded');
      onRefetch();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deletePhotoMut = useMutation({
    mutationFn: (args: { deviceId: string; photoType: 'pre' | 'post'; index: number }) =>
      deleteDevicePhoto(ticket.id, args.deviceId, args.index, args.photoType),
    onSuccess: () => onRefetch(),
    onError: (e) => toastError(parseApiError(e).message),
  });

  const composeMut = useMutation({
    mutationFn: async () => {
      if (composeMode === 'sms') {
        return sendTicketSms(ticket.id, { message: composeBody, toPhone: smsPhone || undefined });
      }
      if (composeMode === 'email') {
        return sendTicketEmail(ticket.id, { to: emailTo, subject: emailSubject, body: composeBody });
      }
      return addNote(ticket.id, {
        body: composeBody,
        visibility: composeMode === 'private' ? 'private' : 'public',
        pinned: false,
      });
    },
    onSuccess: () => {
      setComposeBody('');
      setComposeOpen(false);
      toastSuccess(composeMode === 'sms' ? 'SMS sent' : composeMode === 'email' ? 'Email logged' : 'Note saved');
      onRefetch();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const lineMut = useMutation({
    mutationFn: (args?: {
      description: string;
      unitPrice: number;
      quantity: number;
      type: 'retail' | 'part' | 'custom';
      inventoryItemId?: string;
    }) => {
      const description = args?.description ?? lineDescription;
      const unitPrice = args?.unitPrice ?? Number(linePrice);
      const quantity = args?.quantity ?? Number(lineQty);
      const type =
        args?.type ??
        (selectedInventoryItem ? lineTypeFromItem(selectedInventoryItem) : 'retail');
      const inventoryItemId = args?.inventoryItemId ?? selectedInventoryItem?.id;
      return addLineItem(ticket.id, {
        description,
        type,
        quantity: Number(quantity) || 1,
        unitPrice: Number(unitPrice) || 0,
        discountAmount: 0,
        discountPct: 0,
        taxExempt: false,
        ...(inventoryItemId ? { inventoryItemId } : {}),
      });
    },
    onSuccess: () => {
      setLineDescription('');
      setLineQty('1');
      setLinePrice('0');
      setInventorySearch('');
      setSelectedInventoryItem(null);
      setInventoryDropdownOpen(false);
      toastSuccess('Item added');
      onRefetch();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const feeMut = useMutation({
    mutationFn: (args: { description: string; unitPrice: number; quantity?: number }) =>
      addLineItem(ticket.id, {
        description: args.description,
        type: 'fee',
        quantity: args.quantity ?? 1,
        unitPrice: args.unitPrice,
        discountAmount: 0,
        discountPct: 0,
        taxExempt: true,
      }),
    onSuccess: () => {
      setFeeDescription('');
      setFeeQty('1');
      setFeePrice('0');
      setCustomFeeOpen(false);
      toastSuccess('Fee added');
      onRefetch();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deleteLineMut = useMutation({
    mutationFn: (lineId: string) => deleteLineItem(ticket.id, lineId),
    onSuccess: () => onRefetch(),
  });

  function openComposer(mode?: ComposeMode) {
    if (mode) setComposeMode(mode);
    setComposeOpen(true);
  }

  function viewPortal() {
    if (!ticket.portalToken) return;
    window.open(buildTicketPortalUrl(ticket.portalToken), '_blank', 'noopener,noreferrer');
  }

  async function copyPortalLink() {
    if (!ticket.portalToken) return;
    await copyTicketPortalLink(ticket.portalToken);
    toastSuccess('Portal link copied');
  }

  function sendPortalLink() {
    const phone = detail.customer?.phone ?? ticket.customerPhoneOverride;
    if (!ticket.portalToken) return;
    if (!phone) {
      toastError('Customer phone number required to send portal link');
      return;
    }
    openPortalSmsCompose(phone, ticket.portalToken);
  }

  function applyInventoryItem(item: InventoryItemListRow, autoAdd = false) {
    setSelectedInventoryItem(item);
    setLineDescription(String(item.name ?? 'Inventory item'));
    setLinePrice(String(item.retailPrice ?? '0'));
    setInventorySearch('');
    setInventoryDropdownOpen(false);
    if (autoAdd) {
      lineMut.mutate({
        description: String(item.name ?? 'Inventory item'),
        unitPrice: Number(item.retailPrice ?? 0),
        quantity: Number(lineQty) || 1,
        type: lineTypeFromItem(item),
        inventoryItemId: item.id,
      });
    }
  }

  async function handleInventoryEnter(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const code = inventorySearch.trim();
    if (!code) return;
    try {
      const result = await scanBarcode(code);
      if (result.type === 'item' && result.data) {
        const raw = result.data as Record<string, unknown>;
        const item: InventoryItemListRow = {
          id: String(raw.id),
          name: String(raw.name ?? 'Inventory item'),
          category: String(raw.category ?? 'retail_accessory'),
          barcodeInternal: (raw.barcodeInternal as string | null | undefined) ?? null,
          qtyOnHand: String(raw.qtyOnHand ?? '0'),
          retailPrice: String(raw.retailPrice ?? '0'),
          lowStock: Boolean(raw.lowStock),
        };
        applyInventoryItem(item, true);
        return;
      }
      if (result.type === 'serial' && result.data) {
        toastError('Serialized items must be added from the repair parts panel');
        return;
      }
    } catch {
      // fall through to search results
    }
    const first = inventoryPickerQuery.data?.data?.[0];
    if (first) {
      applyInventoryItem(first, true);
      return;
    }
    toastError('No matching inventory item');
  }

  function submitLine(event: FormEvent) {
    event.preventDefault();
    if (!lineDescription.trim()) return;
    lineMut.mutate();
  }

  function submitCustomFee(event: FormEvent) {
    event.preventDefault();
    if (!feeDescription.trim()) return;
    feeMut.mutate({
      description: feeDescription.trim(),
      unitPrice: Number(feePrice),
      quantity: Number(feeQty) || 1,
    });
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1 space-y-6">
        {predictiveAlerts.length > 0 ? (
          <PredictiveAlertsBanner
            ticketId={ticket.id}
            alerts={predictiveAlerts}
            onDismiss={() => onDismissPredictiveAlerts?.()}
            onNoteAdded={onRefetch}
          />
        ) : null}

        {ticket.originalStoreId ? (
          <TicketTransferBanner
            originalStoreName={ticket.originalStoreName}
            transferredAt={ticket.transferredAt}
          />
        ) : null}

        <div className="sticky top-16 z-20 -mx-1 bg-slate-50 px-1 pb-2 pt-1">
          <CompactTicketHeader
          ticket={ticket}
          repairs={detail.repairs}
          users={users}
          onStatusChange={onStatusChange}
          onClearOverride={onClearOverride}
          onSwitchToBench={onSwitchToBench}
          onPriorityChange={onPriorityChange}
          onAssign={(userId) => {
            assignTicket(ticket.id, { technicianId: userId })
              .then(() => {
                toastSuccess('Ticket assigned');
                onRefetch();
              })
              .catch((e) => toastError(parseApiError(e).message));
          }}
          onSendSms={() => openComposer('sms')}
          onSendEmail={() => openComposer('email')}
          onSendCareGuide={() => setCareGuideOpen(true)}
          onViewPortal={viewPortal}
          onCreateWarranty={onCreateWarranty}
          onCancel={() => onStatusChange('cancelled')}
          onVoid={onVoid}
          onDelete={onDelete}
          onTransfer={() => setTransferOpen(true)}
          onClone={() => setCloneOpen(true)}
          canTransfer={canTransfer}
          canClone={canClone}
          canVoid={canVoid}
          canDelete={canDelete}
          warrantyActive={warrantyActive}
          onTicketUpdated={onRefetch}
        />
        {multiDevice ? (
          <div className="mt-2">
            <DeviceTabs
              devices={devices}
              selectedId={selectedDevice?.id ?? ''}
              onSelect={selectDevice}
            />
          </div>
        ) : null}
        </div>

        <TicketIntakeContext ticket={ticket} onSaved={onRefetch} />

        {selectedDevice ? (
          <DeviceCardDetail
            key={selectedDevice.id}
            device={selectedDevice}
            ticket={ticket}
            repairs={repairsByDevice[selectedDevice.id] ?? []}
            lineItems={detail.lineItems}
            partsRequests={partsRequests}
            users={users}
            timeEntries={detail.timeEntries}
            ticketId={ticket.id}
            ticketNumber={ticket.ticketNumber}
            conditionTemplate={conditionTemplate}
            suppliedItems={suppliedItems}
            showSuppliedItems
            onRepairAssign={(repairId, userId) => {
              updateRepair(ticket.id, repairId, { assignedTechId: userId })
                .then(onRefetch)
                .catch((e) => toastError(parseApiError(e).message));
            }}
            onRepairComplete={(repairId) => {
              updateRepair(ticket.id, repairId, { status: 'completed' })
                .then(onRefetch)
                .catch((e) => toastError(parseApiError(e).message));
            }}
            onRepairStatusChange={(repairId, status) => {
              updateRepair(ticket.id, repairId, { status })
                .then(onRefetch)
                .catch((e) => toastError(parseApiError(e).message));
            }}
            onRepairRemove={(repairId) => {
              deleteRepair(ticket.id, repairId)
                .then(() => {
                  toastSuccess('Repair removed');
                  onRefetch();
                })
                .catch((e) => toastError(parseApiError(e).message));
            }}
            onOrderPart={onOrderPart}
            onRepairLineDiscount={(lineId, body) => {
              updateLineItem(ticket.id, lineId, body)
                .then(() => {
                  toastSuccess('Discount applied');
                  onRefetch();
                })
                .catch((e) => toastError(parseApiError(e).message));
            }}
            onRepairPriceUpdate={(repairId, quotedPrice, meta) => {
              updateRepair(ticket.id, repairId, {
                quotedPrice,
                ...(meta?.managerOverrideAuthorizedBy
                  ? { managerOverrideAuthorizedBy: meta.managerOverrideAuthorizedBy }
                  : {}),
              })
                .then(() => {
                  toastSuccess('Repair price updated');
                  onRefetch();
                })
                .catch((e) => toastError(parseApiError(e).message));
            }}
            partsByRepair={partsByRepair}
            trackSerialParts={storeQuery.data?.trackSerialParts}
            onRemovePart={(partId) => removePartMut.mutate(partId)}
            onRefetch={() => {
              partsQuery.refetch();
              onRefetch();
            }}
            onPatchDevice={onPatchDevice}
            onRemoveDevice={onRemoveDevice}
            otherDevices={detail.devices.filter((d) => d.id !== selectedDevice.id)}
            onPatchSuppliedItems={onPatchSuppliedItems}
            onUploadPhoto={(deviceId, photoType, dataUrl, caption) =>
              photoMut.mutate({ deviceId, photoType, dataUrl, caption })
            }
            onDeletePhoto={(deviceId, photoType, idx) =>
              deletePhotoMut.mutate({ deviceId, photoType, index: idx })
            }
            currentUserId={user?.id}
          />
        ) : null}

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-bold text-slate-950">Additional Items</h2>
            <button
              type="button"
              className="rounded-xl bg-primary px-3 py-1.5 text-sm font-semibold text-white"
              onClick={() => {
                setFeePanelOpen((open) => !open);
                setCustomFeeOpen(false);
              }}
            >
              + Add Fee
            </button>
          </div>

          {feePanelOpen ? (
            <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Quick Fees</h3>
              <div className="space-y-2">
                {(feePresetsQuery.data ?? []).map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3"
                  >
                    <div>
                      <div className="font-semibold text-slate-950">{preset.name}</div>
                      <div className="text-xs capitalize text-slate-500">
                        {formatCurrency(preset.amount)}
                        {preset.type === 'per_hour' ? ' / hr' : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                      disabled={feeMut.isPending}
                      onClick={() =>
                        feeMut.mutate({
                          description: preset.name,
                          unitPrice: Number(preset.amount),
                          quantity: 1,
                        })
                      }
                    >
                      + Add
                    </button>
                  </div>
                ))}
                {(feePresetsQuery.data ?? []).length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No fee presets configured. Add them in Settings → Tickets.
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="mt-3 text-sm font-semibold text-primary"
                onClick={() => setCustomFeeOpen((open) => !open)}
              >
                {customFeeOpen ? 'Hide custom fee' : '+ Custom Fee'}
              </button>
              {customFeeOpen ? (
                <form className="mt-3 flex flex-wrap gap-2 border-t border-slate-200 pt-3" onSubmit={submitCustomFee}>
                  <input
                    className="min-w-[180px] flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Description"
                    value={feeDescription}
                    onChange={(e) => setFeeDescription(e.target.value)}
                  />
                  <input
                    className="w-24 rounded-xl border border-slate-300 px-2 py-2 text-sm"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Amount"
                    value={feePrice}
                    onChange={(e) => setFeePrice(e.target.value)}
                  />
                  <input
                    className="w-16 rounded-xl border border-slate-300 px-2 py-2 text-sm"
                    value={feeQty}
                    onChange={(e) => setFeeQty(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    disabled={feeMut.isPending}
                  >
                    Add to Ticket
                  </button>
                </form>
              ) : null}
            </div>
          ) : null}

          <form className="mb-4 flex flex-wrap gap-2" onSubmit={submitLine}>
            <div className="relative min-w-[200px] flex-1" ref={inventorySearchWrapRef}>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                placeholder="Scan SKU / search inventory (2+ chars)"
                value={inventorySearch}
                onChange={(e) => {
                  setInventorySearch(e.target.value);
                  setSelectedInventoryItem(null);
                  setInventoryDropdownOpen(true);
                }}
                onFocus={() => setInventoryDropdownOpen(true)}
                onKeyDown={handleInventoryEnter}
                autoComplete="off"
              />
              {inventoryDropdownOpen && inventorySearch.trim().length >= 2 ? (
                <div className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                  {inventoryPickerQuery.isFetching ? (
                    <div className="px-3 py-2 text-sm text-slate-500">Searching…</div>
                  ) : (inventoryPickerQuery.data?.data ?? []).length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-500">No items found</div>
                  ) : (
                    (inventoryPickerQuery.data?.data ?? []).map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => applyInventoryItem(item, true)}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-slate-900">{item.name}</span>
                          <span className="block truncate text-xs text-slate-500">
                            {item.barcodeInternal ?? item.sku ?? item.category}
                            {` · Stock ${item.qtyAvailable ?? item.qtyOnHand}`}
                          </span>
                        </span>
                        <span className="shrink-0 font-semibold text-slate-800">
                          {formatCurrency(item.retailPrice)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>
            <input
              className="min-w-[140px] flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="Description"
              value={lineDescription}
              onChange={(e) => {
                setLineDescription(e.target.value);
                if (selectedInventoryItem) setSelectedInventoryItem(null);
              }}
            />
            <input
              className="w-16 rounded-xl border border-slate-300 px-2 py-2 text-sm"
              value={lineQty}
              onChange={(e) => setLineQty(e.target.value)}
            />
            <input
              className="w-24 rounded-xl border border-slate-300 px-2 py-2 text-sm"
              value={linePrice}
              onChange={(e) => setLinePrice(e.target.value)}
            />
            <button
              type="submit"
              disabled={lineMut.isPending || !lineDescription.trim()}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              + Add
            </button>
          </form>
          {selectedInventoryItem ? (
            <p className="mb-3 text-xs text-slate-500">
              Linked: {selectedInventoryItem.name}
              {selectedInventoryItem.barcodeInternal
                ? ` (${selectedInventoryItem.barcodeInternal})`
                : ''}
            </p>
          ) : null}
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2">Description</th>
                <th className="py-2">Qty</th>
                <th className="py-2">Unit Price</th>
                <th className="py-2">Total</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {additionalLineItems.map((line) => (
                <tr key={line.id} className="border-b border-slate-100">
                  <td className="py-2">{line.description}</td>
                  <td className="py-2">{line.quantity}</td>
                  <td className="py-2">{formatCurrency(line.unitPrice)}</td>
                  <td className="py-2">{formatCurrency(linePreTaxTotal(line))}</td>
                  <td className="py-2 text-right">
                    <button type="button" className="text-red-600" onClick={() => deleteLineMut.mutate(line.id)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <TicketFormsSection
          ticketId={ticket.id}
          customerId={detail.customer?.id ?? ticket.customerId ?? null}
        />

        <div ref={timelineRef}>
          <CommunicationTimeline
            ticketId={ticket.id}
            notes={detail.notes}
            communications={detail.communications ?? []}
            events={detail.recentEvents}
            composeMode={composeMode}
            onComposeModeChange={setComposeMode}
            composeBody={composeBody}
            onComposeBodyChange={setComposeBody}
            smsPhone={smsPhone}
            onSmsPhoneChange={setSmsPhone}
            emailTo={emailTo}
            onEmailToChange={setEmailTo}
            emailSubject={emailSubject}
            onEmailSubjectChange={setEmailSubject}
            onSubmit={() => composeMut.mutate()}
            submitting={composeMut.isPending}
            hideComposer
          />
        </div>
      </div>

      <TicketDetailSidebar
        ticket={ticket}
        customer={detail.customer}
        customerName={customerName}
        estimatedProfit={detail.estimatedProfit}
        partsCost={detail.partsCost}
        store={storeQuery.data}
        partsGroups={partsQuery.data?.data ?? []}
        payments={detail.payments}
        onCheckout={onCheckout}
        onCollectDeposit={onCollectDeposit}
        onPaymentsChanged={onRefetch}
        onViewPortal={viewPortal}
        onCopyPortalLink={copyPortalLink}
        onSendPortalLink={sendPortalLink}
        onLogTime={() => timelineRef.current?.scrollIntoView({ behavior: 'smooth' })}
        onOrderPart={() => {
          const repair = detail.repairs[0];
          if (repair) onOrderPart(repair);
        }}
        onCreateWarranty={onCreateWarranty}
        onSendSms={() => openComposer('sms')}
        onSendEmail={() => openComposer('email')}
        {...(onCollectPreWaiver ? { onCollectPreWaiver } : {})}
        {...(onCollectPostWaiver ? { onCollectPostWaiver } : {})}
        warrantyActive={warrantyActive}
        estimatesSection={
          <TicketEstimatesPanel ticketId={ticket.id} repairs={detail.repairs} />
        }
      />

      <SendCareGuideModal
        open={careGuideOpen}
        onClose={() => setCareGuideOpen(false)}
        ticketId={ticket.id}
        ticketNumber={ticket.ticketNumber}
      />

      {cloneOpen ? (
        <CloneTicketModal
          ticketId={ticket.id}
          ticketNumber={ticket.ticketNumber}
          currentCustomerId={ticket.customerId ?? null}
          open={cloneOpen}
          onClose={() => setCloneOpen(false)}
        />
      ) : null}
      <TransferTicketModal
        open={transferOpen}
        ticketId={ticket.id}
        ticketNumber={ticket.ticketNumber}
        onClose={() => setTransferOpen(false)}
        onSuccess={(result) => {
          if (result?.newTicketId) {
            navigate(`/tickets/${result.newTicketId}`);
          } else {
            onRefetch();
          }
        }}
      />

      {composeOpen ? null : (
        <button
          type="button"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-[#164477] lg:right-[22rem]"
          onClick={() => openComposer()}
          aria-label="Write a note"
        >
          <MessageSquarePlus className="h-5 w-5" />
          Note
        </button>
      )}

      <BottomSheet
        open={composeOpen}
        title="Quick note"
        onClose={() => setComposeOpen(false)}
      >
        <TicketComposeFields
          composeMode={composeMode}
          onComposeModeChange={setComposeMode}
          composeBody={composeBody}
          onComposeBodyChange={setComposeBody}
          smsPhone={smsPhone}
          onSmsPhoneChange={setSmsPhone}
          emailTo={emailTo}
          onEmailToChange={setEmailTo}
          emailSubject={emailSubject}
          onEmailSubjectChange={setEmailSubject}
          onSubmit={() => composeMut.mutate()}
          submitting={composeMut.isPending}
        />
      </BottomSheet>
    </div>
  );
}
